/**
 * `useElapsedTick` — 1Hz heartbeat that re-renders the caller while
 * `isRunning` is true. Forced extraction from `AgentsPanel.tsx` (issue
 * #517 fix shipped in alpha.48) so the C3 agents-panel uplift and
 * C2's activity-banner can share a single source of truth for the
 * "live duration display" affordance.
 *
 * Background — issue #517: pre-fix, agent rows computed
 * `elapsed = now - startedAt` only at render time, and the panel only
 * re-rendered when the chat-reducer dispatched a new chunk for the
 * agent (anything from a few seconds between chunks to minutes during
 * a long Read/Write). A `RUNNING 8m 30s` that doesn't tick reads as
 * "the build has stalled" even when work is actively underway.
 *
 * Strategy: subscribe to a 1s `setInterval` that bumps a local `tick`
 * state ONLY while `isRunning` — when the row's status flips to
 * `success` / `error` / `interrupted`, the interval is cleared so we
 * don't waste timer slots re-rendering a static final duration. One
 * interval per running row is fine for the panel's typical 1-5
 * concurrent agents; if the panel ever scales to many concurrent
 * running agents the hook can be migrated to a single shared
 * broadcaster.
 *
 * Used by `AgentRowCard` for specialists and inlined into
 * `MainAgentRowCard` for the orchestrator (any active state). Will
 * also be consumed by C2's `ActivityBanner` (#668) for the elapsed
 * counter on the active row.
 *
 * Returns the current tick count — callers typically ignore the
 * value and use it solely as a re-render trigger; the actual
 * elapsed-time math (e.g. `formatDuration`) reads `Date.now()` on
 * every render.
 *
 * `intervalMs` defaults to 1000 (the 1Hz elapsed-timer case). Callers
 * that only need to re-derive a coarse time-based state — e.g. the
 * build-watch status row flipping to "stalled" once `lastActivityAt`
 * ages past its window with no new poll data (#3025) — pass a coarser
 * interval (5000) so a genuine/hard stall still surfaces WITHOUT a
 * per-second re-render loop.
 */

import { useEffect, useState } from 'react';

export function useElapsedTick(isRunning: boolean, intervalMs = 1000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [isRunning, intervalMs]);
  return tick;
}
